/**
 * Portal do Cliente (Onda 1): a ponte com o app das lojas (Capacitor), sem depender de pacote nativo no build web.
 *
 * Um front só: no navegador (PWA) a sessão é o cookie HttpOnly do portal; embalado pelo Capacitor (iOS, iPadOS, macOS e
 * Android) a API fica em outra origem (`capacitor://localhost` ou `https://localhost`) e a sessão vira o token `mp_...`
 * guardado no ARMAZENAMENTO SEGURO do aparelho (Keychain no iOS e no Mac, Keystore no Android), enviado como Bearer. Nada
 * aqui usa API exclusiva de navegador sem alternativa: o plugin nativo é procurado em tempo de execução
 * (`window.Capacitor.Plugins`) e, sem ele, vale o caminho do navegador.
 *
 * Plugins previstos no empacotamento (docs/OPERACAO.md, "Portal do Cliente: app nas lojas"): `@capacitor/app` (deep link),
 * `capacitor-secure-storage-plugin` (token), `@capacitor/push-notifications` (APNs e FCM).
 */

import { caminhoInterno } from './destino';

interface PluginsNativos {
  App?: { addListener(evento: 'appUrlOpen', fn: (e: { url: string }) => void): Promise<{ remove(): void }> | { remove(): void } };
  SecureStoragePlugin?: { get(o: { key: string }): Promise<{ value: string }>; set(o: { key: string; value: string }): Promise<unknown>; remove(o: { key: string }): Promise<unknown> };
  PushNotifications?: unknown;
}

interface CapacitorGlobal {
  isNativePlatform?(): boolean;
  getPlatform?(): string;
  Plugins?: PluginsNativos;
}

const capacitor = (): CapacitorGlobal | undefined => (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor;

/** Rodando dentro do app das lojas (e não no navegador). */
export const ehNativo = (): boolean => !!capacitor()?.isNativePlatform?.();
export const plataforma = (): string => capacitor()?.getPlatform?.() ?? 'web';

/** Endereço da API: relativo no navegador (mesma origem do portal); absoluto no app nativo. */
export const baseDaApi = (): string => (ehNativo() ? String(import.meta.env.VITE_PORTAL_API ?? 'https://portal.malgo.com.br').replace(/\/$/, '') : '');

const CHAVE_TOKEN = 'malgo.portal.sessao';
const CHAVE_VINCULO = 'malgo.portal.vinculo';
let tokenEmMemoria: string | null = null;

/** Armazenamento seguro do aparelho (só no app nativo). No navegador não guarda nada: a sessão é o cookie HttpOnly. */
export const cofre = {
  async lerToken(): Promise<string | null> {
    if (!ehNativo()) return null;
    if (tokenEmMemoria) return tokenEmMemoria;
    const s = capacitor()?.Plugins?.SecureStoragePlugin;
    tokenEmMemoria = s ? await s.get({ key: CHAVE_TOKEN }).then((r) => r.value, () => null) : null;
    return tokenEmMemoria;
  },
  async gravarToken(token: string) {
    tokenEmMemoria = token;
    await capacitor()?.Plugins?.SecureStoragePlugin?.set({ key: CHAVE_TOKEN, value: token });
  },
  async apagarToken() {
    tokenEmMemoria = null;
    await capacitor()?.Plugins?.SecureStoragePlugin?.remove({ key: CHAVE_TOKEN }).catch(() => undefined);
  },
  /** Vínculo do pedido de código (o link do e-mail só entra no aparelho que pediu). */
  async gravarVinculo(v: string) {
    await capacitor()?.Plugins?.SecureStoragePlugin?.set({ key: CHAVE_VINCULO, value: v });
  },
  async lerVinculo(): Promise<string | null> {
    const s = capacitor()?.Plugins?.SecureStoragePlugin;
    return s ? s.get({ key: CHAVE_VINCULO }).then((r) => r.value, () => null) : null;
  },
};

/**
 * Deep links: `https://portal.malgo.com.br/<rota>?<busca>#<token>` (link universal no iOS, App Link no Android) e
 * `malgo://<rota>` abrem a mesma rota do app. Devolve o caminho interno (com a busca e o fragmento) ou null se o endereço
 * não é do portal. Review r53: os dois ramos passam pelo mesmo validador do destino (`caminhoInterno`: nada de `//host`,
 * nem antes nem depois de normalizar; SEG-B2) e a busca fica (o `?cliente=` do link da fatura; VIS-B2 = SPEC-B2).
 */
export function rotaDoLink(url: string, hostsPortal: string[] = ['portal.malgo.com.br', 'portal-sandbox.malgo.com.br']): string | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  let caminho: string | null = null;
  if (u.protocol === 'malgo:') {
    // malgo://entrar#token: o "host" é o primeiro segmento da rota.
    const rota = `/${[u.host, u.pathname.replace(/^\/+/, '')].filter(Boolean).join('/')}`;
    caminho = /^\/[a-z0-9/_-]*$/i.test(rota) ? caminhoInterno(`${rota}${u.search}`) : null;
  } else if (u.protocol === 'https:' && hostsPortal.includes(u.hostname)) {
    caminho = caminhoInterno(`${u.pathname}${u.search}`);
  }
  return caminho ? `${caminho}${u.hash}` : null;
}

/** Ouve os deep links do app nativo (no navegador não faz nada). */
export function ouvirLinks(navegar: (rota: string) => void): () => void {
  const app = capacitor()?.Plugins?.App;
  if (!app) return () => undefined;
  let remover: (() => void) | null = null;
  void Promise.resolve(
    app.addListener('appUrlOpen', (e) => {
      const rota = rotaDoLink(e.url);
      if (rota) navegar(rota);
    }),
  ).then((h) => (remover = () => h.remove()));
  return () => remover?.();
}
